/**
 * Referral-code capture.
 *
 * The first time a visitor lands with `?ref=<code>`, we stash the code
 * in localStorage so it survives the Privy redirect dance (and any
 * subsequent page refreshes). On the first successful `/auth/privy`
 * exchange the code is forwarded to the server which binds it as the
 * permanent referrer for the wallet.
 *
 * Code shape: first 10 chars of the referrer's wallet — base58 on Solana
 * (CASE-SENSITIVE), hex on the legacy EVM path. Self-referrals are blocked
 * server-side, so we don't bother sanity-checking against the user's own
 * wallet here.
 */

const STORAGE_KEY = "hupper.referral.code";
const TTL_MS = 30 * 86_400_000; // 30 days

interface StoredRef {
  code: string;
  capturedAtMs: number;
  /** True once we've sent it to /auth/privy successfully. After that
   *  we leave it on disk for diagnostics but stop forwarding it. */
  bound: boolean;
}

function loadRaw(): StoredRef | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredRef;
    if (Date.now() - parsed.capturedAtMs > TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function save(rec: StoredRef): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rec));
  } catch { /* private mode */ }
}

/**
 * Read `?ref=` from the current URL. If present + valid, persist it
 * (overwriting any older capture — most recent click wins) and strip
 * the param from the URL so it doesn't show in the address bar.
 *
 * Call once on app mount, before any auth attempt.
 */
export function captureReferralFromUrl(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    const raw = url.searchParams.get("ref");
    if (!raw) return;
    // Preserve case: Solana codes are base58 (case-sensitive) and contain
    // uppercase letters + letters beyond a-f. Lowercasing corrupts them and a
    // hex-only regex would drop every Solana code. Accept the base58/hex
    // alphabet as-is; the server resolves the exact case against the directory.
    const code = raw.trim();
    if (!/^[0-9A-Za-z]{8,12}$/.test(code)) return; // 10 chars in practice, allow 8-12
    save({ code, capturedAtMs: Date.now(), bound: false });
    url.searchParams.delete("ref");
    window.history.replaceState(null, "", url.toString());
  } catch { /* ignore */ }
}

/** Return the captured code if we haven't bound it yet. */
export function pendingReferralCode(): string | null {
  const rec = loadRaw();
  if (!rec || rec.bound) return null;
  return rec.code;
}

/** Mark the captured code as bound — called after `/auth/privy` resolves. */
export function markReferralBound(): void {
  const rec = loadRaw();
  if (!rec || rec.bound) return;
  save({ ...rec, bound: true });
}
